<template>
  <modal-card
    title="Interviews"
    fixed
    padding
    :loading="loading || saving"
    @close="$emit('close')"
  >
    <div v-if="!success">
      <v-row wrap>
        <v-col
          v-for="(interview, idx) in interviews"
          :key="idx"
          cols="12"
          sm="6"
          md="4"
        >
          <VectorInterviewCard
            :interview="interview"
            :disabled="!contact.email"
            :scheduled="isInterviewScheduled(interview)"
            :class="{ selected: interview.selected }"
            @click.native="toggleInterviewSelect(interview)"
            @addToInterview="addToInterview(interview)"
          />
        </v-col>
      </v-row>
    </div>

    <InterviewSuccessMessage
      v-else
      :title="successMessage.title"
      :message="successMessage.message"
    />

    <template #actions>
      <v-btn
        v-if="!success"
        color="appMain"
        class="light--text"
        :disabled="!selectedInterviews.length || saving"
        @click="sendInterviewOffer"
      >
        Send Interview Times
      </v-btn>
      <v-btn
        v-else
        color="appMain"
        class="light--text"
        @click="$emit('nextContact')"
      >
        Finish
      </v-btn>
    </template>
  </modal-card>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';

import { format, addMinutes } from 'date-fns';
import { es } from 'date-fns/locale';

import intersection from 'lodash/intersection';
import clone from 'lodash/clone';

import { RECRUITING_STATUS } from '../../recruitingConstants';

import VectorInterviewCard from './VectorInterviewCard.vue';
import InterviewSuccessMessage from './InterviewSuccessMessage.vue';

export default {
  name: 'VectorSelectInterview',
  components: {
    VectorInterviewCard,
    InterviewSuccessMessage,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    rawInterviews: {
      type: Array,
      default: () => [],
    },
    zipTimezone: String,
    contactId: String,
    loading: Boolean,
  },
  data() {
    return {
      interviews: [],
      saving: false,
      success: false,
      successType: null, // 'scheduled' or 'offer'
    };
  },
  watch: {
    rawInterviews: {
      immediate: true,
      handler() {
        this.resetState();
        this.prepareInterviews();
      },
    },
  },
  computed: {
    ...mapGetters('fields', ['getSingleField']),
    ...mapGetters('vector', ['getSingleVectorSnippet']),
    selectedInterviews() {
      return this.interviews.filter((i) => i.selected);
    },

    contactTags() {
      return this.contact?.tags || [];
    },

    successMessage() {
      if (this.successType === 'scheduled') {
        return {
          title: 'Interview Scheduled!',
          message:
            'The contact has been added to the interview and confirmation has been sent.',
        };
      }
      return {
        title: 'Interview Times Sent!',
        message: 'Interview time options have been sent to the contact.',
      };
    },
  },
  methods: {
    ...mapActions([
      'updateContact',
      'updateContactField',
      'compileTextForContact',
    ]),
    ...mapActions('vector', ['getOfficeData']),

    isInterviewScheduled({ interviewId }) {
      return this.contact.interview_id === interviewId;
    },

    toggleInterviewSelect(interview) {
      Vue.set(interview, 'selected', !interview.selected);
    },

    async addToInterview(interview) {
      const { interviewId } = interview || {};

      if (!interviewId) return;

      try {
        this.saving = true;

        await this.updateContactField({
          contactId: this.contactId,
          field: 'interview_id',
          value: interviewId,
        });

        await this.sendInterviewConfirmation(interview);

        await this.updateContactField({
          contactId: this.contactId,
          field: 'status',
          value: RECRUITING_STATUS.REACHED,
        });

        this.successType = 'scheduled';
        this.success = true;
      } finally {
        this.saving = false;
      }
    },

    sendInterviewOffer() {
      const message = this.prepareOfferText();
      this.$emit('sendMessage', { message, type: 'offer' });

      this.successType = 'offer';
      this.success = true;
    },

    async sendInterviewConfirmation(interview) {
      const officeInfo = await this.getOfficeData(this.contact);
      const { rel_time, just_time, interviewLocation } = interview || {};

      const data = {
        interview_time: rel_time || '',
        interview_just_time: just_time || '',
        office_address: '',
        helpful_hint_1: '',
        helpful_hint_2: '',
        fully_remote: false,
        far: false,
        remote: false,
        ...officeInfo,
      };

      if (this.contact) {
        data.far = this.contact.far;
        data.remote = this.contact.remote;
      }

      const snippetShortcut = this.getSnippetShortcut(interviewLocation);
      const snippet = await this.loadSnippet(snippetShortcut);
      const compiled = await this.compileTextForContact({
        contactId: this.contactId,
        content: snippet?.text_content,
        inputs: {
          ...data,
        },
      });

      this.$emit('sendMessage', {
        message: this.normalizeSMSContent(compiled),
        type: 'scheduled',
      });
    },

    normalizeSMSContent(content) {
      if (!content) return '';

      return (
        content
          .replace(/\r\n/g, '\n')
          // Remove lines that contain only spaces or tabs
          .replace(/^[\t ]+$/gm, '')
          // Replace 2+ newlines with a single newline
          .replace(/\n{2,}/g, '\n')
          // Trim whitespace at start/end
          .trim()
      );
    },

    async loadSnippet(snippetKey) {
      let shortcut = 'vmc.interview_confirm';
      if (snippetKey) {
        shortcut += `.${snippetKey}`;
      }

      const snippet = this.getSingleVectorSnippet(shortcut);
      if (snippet) return snippet;

      if (snippetKey === 'default') {
        return {
          text_content: 'Default interview confirmation snippet not found.',
        };
      }

      // try to load default snippet
      return await this.loadSnippet('default');
    },

    isSpanish() {
      if (!this.contactTags.length) return false;

      const should_have = ['es', 'es_priapp', 'es_iapp', 'es_pr'];
      return intersection(should_have, this.contactTags).length > 0;
    },

    getFirstMatchingTag(arr) {
      return this.contactTags.find((t) => {
        return arr.indexOf(t) !== -1;
      });
    },

    // returns language.location.variation | location.variation | location
    getSnippetShortcut(location) {
      const { remote, source_main: sourceMain } = this.contact;

      // Get location tag (apex / class / remote / apex.post / on_demand)
      const getLocationTag = () => {
        if (location) return location;
        if (remote) return 'remote';

        const matchingTag = this.getFirstMatchingTag([
          'apex',
          'apex_post',
          'on_demand',
        ]);
        if (matchingTag === 'apex_post') return 'apex.post';

        return matchingTag || 'class';
      };

      // Get language tag (es / null)
      const getLanguageTag = () => (this.isSpanish() ? 'es' : null);

      // Get variation tag (priapp / ca_priapp / ca_iapp / cl / null)
      const getVariationTag = () => {
        const normalizedSourceMain = (sourceMain || '').toLowerCase();
        const isFromCraigslist = normalizedSourceMain === 'craigs list';
        const isLocationApex = getLocationTag() === 'apex';
        if (isFromCraigslist && isLocationApex) return 'cl';

        return this.getFirstMatchingTag(['priapp', 'ca_priapp', 'ca_iapp']);
      };

      // Combine all tags and return the snippet lookup key
      return [getLanguageTag(), getLocationTag(), getVariationTag()]
        .filter(Boolean)
        .join('.');
    },

    dateAsLocalString(date, tz) {
      return date.toLocaleString('en-US', { timeZone: tz }).split(',')[0];
    },

    isToday(checkDate, tz) {
      const nowDateStr = this.dateAsLocalString(new Date(), tz);
      const checkDateStr = this.dateAsLocalString(checkDate, tz);
      return nowDateStr === checkDateStr;
    },

    prepareInterviews() {
      const isSpanish = this.isSpanish();
      const locale = isSpanish ? es : null;
      const dateFormat = isSpanish ? 'EEE, MMM d' : 'EEE, MMM do';
      const remoteWindow = 60;

      const labels = {
        conjunction: isSpanish ? 'a las' : 'at',
        between: isSpanish ? 'entre' : 'between',
        today: isSpanish ? 'hoy' : 'Today',
      };

      this.interviews = (this.rawInterviews || []).map((i, idx) => {
        let interview = clone(i);

        const ts = interview.startTime || interview.startTimestamp;
        const interviewLocation = interview.interviewLocation;
        const tz = this.zipTimezone || interview.timezone;
        const date = new Date(ts);
        const isToday = this.isToday(date, tz);
        const intDate = new Date(
          date.toLocaleString('en-US', {
            timeZone: tz,
          })
        );

        const timeFormat = "h:mmaaaaa'm'";

        const isApex =
          interviewLocation === 'apex' ||
          !!this.getFirstMatchingTag(['apex', 'apex_post']);
        const isClass =
          interviewLocation === 'class' ||
          !!this.getFirstMatchingTag(['class']);
        const neitherApexNorClass = !isApex && !isClass;

        if (neitherApexNorClass && this.contact.remote) {
          interview.just_time = [
            labels.between,
            format(intDate, timeFormat, { locale }),
            '-',
            format(addMinutes(intDate, remoteWindow), timeFormat, { locale }),
          ].join(' ');
        } else {
          interview.just_time = [
            labels.conjunction,
            format(intDate, timeFormat, { locale }),
          ].join(' ');
        }

        interview.time = [
          isToday ? labels.today : format(intDate, dateFormat, { locale }),
          interview.just_time,
        ].join(' ');

        interview.selected = idx < 2;
        interview.rel_time = interview.time;
        interview.id = interview.interviewId;

        return interview;
      });
    },

    resetState() {
      this.success = false;
      this.successType = null;
    },

    prepareOfferText() {
      const interviews = this.selectedInterviews;
      const interviewsLength = interviews.length;
      const spanish = this.isSpanish();
      let txt = '',
        i;

      // spanish version
      if (spanish) {
        if (interviewsLength === 1) {
          i = interviews[0];
          txt = 'Parece que tenemos una apertura en ' + i.rel_time + '. ';
          txt += 'Es posible que puedas atender?';
        } else {
          txt = 'Resulta que te puedo coordinar para ';
          txt += interviews.map((i) => i.rel_time).join(' o ');
          txt += '. Cual prefieres?';
        }
      }

      // english version
      else {
        if (interviewsLength === 1) {
          i = interviews[0];
          txt = 'Looks like we have one opening on ' + i.rel_time + '. ';
          txt += 'Is there any way you can make that?';
        } else {
          txt = 'Turns out I can get you in either ';
          txt += interviews.map((i) => i.rel_time).join(' or ');
          txt += '. Which do you want to do?';
        }
      }

      return txt;
    },
  },
};
</script>
